/**
 * @module app.core
 */

import { autoinject, bindable, bindingMode } from 'aurelia-framework';
import { ComponentBase, IForm } from 'app/shared/lib/essette-site';
import { ICorrespondenceConfig } from 'app/core/models/essette-interfaces';
import { Toaster } from 'app/shared/lib/toaster';
import { CorrespondenceService } from 'app/core/services/correspondence-service';
import { apiCall } from 'app/shared/lib/essette-api';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { IMemberSearchFilters, MemberService, PhoneNumberSystemCode } from 'app/cd/services/member-service';
import { validatePhoneNumber } from 'app/shared/lib/validation/validation-utilities';
import { ProviderService } from 'app/cd/services/provider-service';
import { I18N } from 'aurelia-i18n';
import * as Core from 'app/core/models/essette-api';
import * as CD from 'app/cd/models/essette-api';

interface IExistingContact {
    display: string;
    recipient: string;
    number: string;
    extension?: string | null;
}

@autoinject
export class ForwardLetterCopy extends ComponentBase implements IForm {

    ////****************** Fields and Properties ************************************////
    @bindable({ defaultBindingMode: bindingMode.toView })
    protected letterCopy: Core.CorrespondenceLetterCopy = {};

    @bindable({ defaultBindingMode: bindingMode.oneTime })
    public correspondenceConfig: ICorrespondenceConfig | null = null;

    protected contactOption = 'existingContact';
    protected contacts: IExistingContact[] = [];
    protected selectedContact: IExistingContact | null = null;
    protected faxToName: string | null = null;
    protected faxToNumber: Core.PhoneNumber | null = null;
    protected maxFaxToNameLength = 100;

    protected validatePhoneNumber = validatePhoneNumber;

    ////****************** Element References ***************************************////


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly service: CorrespondenceService,
        private readonly memberService: MemberService,
        private readonly providerService: ProviderService,
        private readonly toaster: Toaster,
        private readonly i18n: I18N,
        ...rest: any[]) {
        super(...rest);
    }

    protected bind(): void {
        if(this.correspondenceConfig) this.service.configure(this.correspondenceConfig);
    }

    ////****************** Events ***************************************************////
    protected async letterCopyChanged(): Promise<void> {
        if(!this.letterCopy.id) return;
        this.reset();
        await this.loadLookups();
    }

    ////****************** Functions ************************************************////
    public hasChanges(): boolean {
        return false;
    }

    public reset(): void {
        this.contacts.splice(0);
        this.contactOption = 'existingContact';
        this.selectedContact = null;
        this.faxToName = null;
        this.faxToNumber = null;
        return;
    }

    public async submit(): Promise<boolean> {
        return await this.forwardLetterCopy();
    }

    protected configureMemberContacts(primaryFaxNumber?: CD.MemberPhoneNumber, alternateFaxNumbers?: CD.MemberPhoneNumber[]): void {
        this.contacts.splice(0);
        if(primaryFaxNumber?.$display && primaryFaxNumber.member?.name?.full && primaryFaxNumber.number) {
            this.contacts.push({
                display: `${primaryFaxNumber.member.name.full}  ${primaryFaxNumber.$display} (Primary Fax Number)`,
                recipient: primaryFaxNumber.member.name.full,
                number: primaryFaxNumber.number,
                extension: primaryFaxNumber.extension ?? null
            });
        }
        if(alternateFaxNumbers?.length) {
            alternateFaxNumbers.forEach(faxNumber => {
                if(faxNumber.$display && faxNumber.member?.name?.full && faxNumber.number) {
                    this.contacts.push({
                        display: `${faxNumber.member.name.full}  ${faxNumber.$display} (Alternate Fax Number)`,
                        recipient: faxNumber.member.name.full,
                        number: faxNumber.number,
                        extension: faxNumber.extension ?? null
                    });
                }
            });
        }
    }

    protected configureProviderContacts(providerLocation?: CD.ProviderLocation, providerContacts?: CD.ProviderContact[]): void {
        this.contacts.splice(0);
        if(providerLocation?.description && providerLocation.facility?.phoneNumber?.fax?.$display && providerLocation.facility.phoneNumber.fax.number) {
            this.contacts.push({
                display: `${providerLocation.description}  ${providerLocation.facility.phoneNumber.fax.$display}`,
                recipient: providerLocation.description,
                number: providerLocation.facility.phoneNumber.fax.number,
                extension: null
            });
        }
        if(providerContacts?.length) {
            providerContacts.forEach(providerContact => {
                if(providerContact.name?.full && providerContact.phoneNumber?.fax?.$display && providerContact.phoneNumber.fax.number && providerContact.location?.facility?.$display) {
                    this.contacts.push({
                        display: `${providerContact.name.full}, ${providerContact.location.facility.$display}  ${providerContact.phoneNumber?.fax?.$display}`,
                        recipient: `${providerContact.name.full}, ${providerContact.location.facility.$display}`,
                        number: providerContact.phoneNumber.fax.number,
                        extension: null
                    });
                }
            });
        }
    }

    private buildLetterCopy(): Core.CorrespondenceLetterCopy | null {
        let faxToName = '';
        let faxToNumber = '';
        if(this.contactOption === 'existingContact') {
            // Existing Contact
            if(!this.selectedContact?.recipient || !this.selectedContact?.number) return null;
            faxToName = this.selectedContact.recipient;
            faxToNumber = this.selectedContact.number;
            if(this.selectedContact.extension) faxToNumber += this.selectedContact.extension;
        } else {
            // New Contact
            if(!this.faxToName || !this.faxToNumber?.number) return null;
            faxToName = this.faxToName.trim();
            faxToNumber = this.faxToNumber.number.replace(/\D/g, ''); // remove all non-digits
            if(this.faxToNumber.extension?.trim()) faxToNumber += this.faxToNumber.extension.trim();
        }

        // faxToName could exceed 100 with existing contacts, in which case cut it down
        if(faxToName.length > this.maxFaxToNameLength) faxToName = faxToName.substring(0, this.maxFaxToNameLength);

        this.letterCopy.faxToName = faxToName;
        this.letterCopy.faxToNumber = faxToNumber;

        return this.letterCopy;
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadLookups(): Promise<void> {
        const isMember = this.letterCopy.delivered?.to?.type?.code === "CD.Member";
        const targetId = this.letterCopy.delivered?.to?.id;
        if(!this.letterCopy.id || !targetId) return;

        try {
            this.state.beginLoad();

            const options: Partial<IQueryLoadOptions> = { all: true };

            if(isMember) {
                const includes = ['member'];

                const searchFilters: IMemberSearchFilters = {
                    alternateTypes: true,
                    numberIsFax: true
                };

                const [primaryResponse, secondaryResponse] = await Promise.all([
                    this.memberService.loadMemberPhoneNumbers(targetId, PhoneNumberSystemCode.Fax, options, includes),
                    this.memberService.loadMemberPhoneNumbers(targetId, undefined, options, includes, [], searchFilters)
                ]);

                this.configureMemberContacts(primaryResponse.results[0], secondaryResponse.results);
            } else {
                const providerLocationIncludes = ['facility', 'facility.phoneNumber.fax'];
                const providerContactsIncludes = ['location', 'location.facility', 'phoneNumber.fax'];
                const [primaryResponse, secondaryResponse] = await Promise.all([
                    this.providerService.loadProviderLocation(targetId, providerLocationIncludes),
                    this.providerService.loadProviderContactsByLocation(targetId, options, providerContactsIncludes)
                ]);

                this.configureProviderContacts(primaryResponse.results[0], secondaryResponse.results);
            }
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async forwardLetterCopy(): Promise<boolean> {
        const letterCopy = this.buildLetterCopy();
        if(!letterCopy || !letterCopy.id) return false;

        let isSuccessful = false;
        try {
            this.state.beginSave();
            const response = await this.service.resendLetterCopy(letterCopy);
            isSuccessful = response.isSuccessful;
        } catch(error) {
            throw error;
        } finally {
            const toast = this.toaster.toast()
                .class(isSuccessful ? 'ess-toast-info' : 'ess-toast-error')
                .icon(isSuccessful ? 'ui:icons.info' : 'ui:icons.error')
                .message(this.i18n.tr(isSuccessful ? 'core:correspondenceLetter.notifications.queuedProcessing' : 'core:correspondenceLetter.notifications.failedProcessing'));
            toast.show();
            this.state.endSave();
            return isSuccessful;
        }
    }
}
